
import Vue from 'vue';
import { FragmentPicture } from '@core/types/fragment';
import buildPictureUrl from '@core/utils/buildPictureUrl';
import BaseIcon from '@core/components/UI/BaseIcon.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import { emittedPayload as PinchZoomPayload, PinchZoom } from '@gallery/utils/PinchZoom';

type PictureSrcset = { xs: string; sm: string; md: string; lg: string; xl: string };

interface Props {
  id: string;
  cropperData: Record<string, any>;
  picture: Record<string, keyof FragmentPicture>;
  pictureSrcset: PictureSrcset | null;
  fallbackAltText: string;
  authors: Array<string> | null;
  credit: string | null;
}

interface Data {
  sizesData: Record<string, { media: string }>;
  scaledIn: boolean;
  pinchZoom: PinchZoom | undefined;
  zoomScale: number;
}
interface Computed {
  src: string | null;
  srcset: PictureSrcset | Record<string, unknown>;
  caption: string | null;
  description: string | null;
}

interface Methods {
  getSrc: () => string | null;
  getSrcset: () => PictureSrcset | Record<string, unknown>;
  getNewSrc: () => string | null;
  getNewSrcset: () => string | null | any;
  resetScale: () => void;
  changeScale: (zoom: boolean) => void;
  mountZoomFeatures: () => void;
}

const sizesData = {
  xl: {
    media: '(min-width: 1200px)',
  },
  lg: {
    media: '(min-width: 992px)',
  },
  md: {
    media: '(min-width: 768px)',
  },
  sm: {
    media: '(min-width: 576px)',
  },
  xs: {
    media: '(min-width: 240px)',
  },
};

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BaseIcon,
    AdFormBanner,
  },
  data() {
    return {
      sizesData,
      scaledIn: false,
      zoomProperties: {},
      zoomScale: 1,
      pinchZoom: undefined,
    };
  },
  props: {
    id: {
      type: String,
      required: false,
    },
    cropperData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    picture: {
      type: Object,
      required: false,
    },
    pictureSrcset: {
      type: Object,
      default: null,
      required: false,
    },
    fallbackAltText: {
      type: String,
      required: false,
      default: '',
    },
    authors: {
      type: Array,
    },
    credit: {
      type: String,
    },
  },
  computed: {
    caption() {
      return this.picture?.caption || this.fallbackAltText || null;
    },
    description() {
      const authors = this.authors?.filter(Boolean).join(', ');
      const result = [authors, this.credit].filter(Boolean).join('/');

      return result;
    },
    src() {
      return this.id ? this.getNewSrc() : this.getSrc();
    },
    srcset() {
      return this.id ? this.getNewSrcset() : this.getSrcset();
    },
  },
  methods: {
    getSrc() {
      const { srcset } = this;
      const isEmpty = Object.keys(srcset).length === 0;
      if (isEmpty) {
        return '';
      }

      // Find smallest picture for default img src
      return (srcset['xs'] || srcset['md'] || srcset['sm'] || srcset['lg']) as string;
    },
    getSrcset() {
      const srcset: PictureSrcset | Record<string, unknown> = {};
      const sizeList = this.pictureSrcset;

      if (!sizeList) {
        return srcset;
      }

      // Create srcset for picture source tag
      for (const screenSize of Object.keys(sizeList)) {
        const size = sizeList[screenSize as keyof PictureSrcset];
        const picture = this.picture[size];

        if (picture) {
          srcset[screenSize as keyof PictureSrcset] = picture;
        }
      }

      return srcset as PictureSrcset;
    },
    getNewSrc() {
      const cropperData = { ...this.cropperData };
      const sizeData = cropperData['xs'] || cropperData['sm'] || cropperData['md'] || cropperData['lg'] || cropperData['xl'];

      // TODO! separate srcSet sizes from cropperData after content api v3
      if (sizeData?.customCrop) {
        // add size data and custom crop
        const customCrop = sizeData.customCrop;
        const sizeDataCopy = { ...sizeData };
        delete sizeDataCopy.customCrop;
        return buildPictureUrl({ id: this.id, cropperData: { ...sizeDataCopy, ...customCrop } });
      }
      return buildPictureUrl({ id: this.id, cropperData: { ...sizeData, ...cropperData.base } });
    },
    getNewSrcset() {
      const cropperData = { ...this.cropperData };
      const srcset: Record<string, string | null> = {};

      // Find srcset sizes from cropperData: sm | md | lg
      for (const key of Object.keys(cropperData)) {
        if (this.sizesData[key]) {
          const sizeData = cropperData[key];
          // TODO! separate srcSet sizes from cropperData after content api v3
          if (sizeData?.customCrop) {
            const customCrop = sizeData.customCrop;
            const sizeDataCopy = { ...sizeData };
            delete sizeDataCopy.customCrop;
            srcset[key] = buildPictureUrl({ id: this.id, cropperData: { ...sizeDataCopy, ...customCrop } });
          } else {
            srcset[key] = buildPictureUrl({ id: this.id, cropperData: { ...sizeData, ...cropperData.base } });
          }
        }
      }
      return srcset;
    },
    resetScale() {
      this.zoomScale = 1;
      if (this.pinchZoom) {
        this.pinchZoom.resetScale();
      }
    },
    changeScale(zoom) {
      const maxScale = 5;
      const newScale = zoom ? this.zoomScale + 0.5 : this.zoomScale - 0.5;
      this.zoomScale = newScale <= maxScale ? newScale : this.zoomScale;
      if (this.pinchZoom) {
        this.pinchZoom.changeScale(this.zoomScale);
      }
    },
    mountZoomFeatures() {
      this.zoomScale = 1;

      const zoomProperties = {
        element: this.$refs['contentBlock'] as HTMLDivElement,
        limitZoom: 40,
        onScaleChange: (payload: PinchZoomPayload): void => {
          this.zoomScale = payload.scale;
          this.scaledIn = this.zoomScale > 1;
        },
      };

      this.pinchZoom = new PinchZoom(zoomProperties);
    },
  },
  mounted() {
    this.mountZoomFeatures();
  },
});
